import { useState } from 'react';
import { useDebounce } from 'react-use';

import clsx from 'clsx';
import { matchSorter } from 'match-sorter';

// Hooks
import { useSearchBoxContext } from '@/utils/hooks/context/searchBox/useSearchBoxContext';

// Components
import Icon from '@/components/base/Icon';
import type { GroupsFormattedType } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/GroupsTab';
// Types
import type { SocialProfileFormattedType } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab';

type SearchInputProps = {
    placeholder: string;
};

export function SearchInput<TContext extends SocialProfileFormattedType | GroupsFormattedType>({
    placeholder
}: SearchInputProps) {
    const { dataContext, dispatch } = useSearchBoxContext<TContext>();
    const { searchKeys, initialData, inputId } = dataContext;
    const [searchValue, setSearchValue] = useState('');

    const getFilteredData = (searchValue: string) => {
        if (!searchValue) {
            return initialData;
        }
        return matchSorter(initialData, searchValue, searchKeys);
    };

    useDebounce(
        () => {
            const newFilteredData = getFilteredData(searchValue);
            dispatch({ type: 'SET_FILTERED_DATA', payload: newFilteredData });
            dispatch({ type: 'SET_INPUT_VALUE', payload: searchValue });
        },
        200,
        [searchValue]
    );

    return (
        <div className="flex items-center gap-2 px-5 text-xs">
            <Icon icon="search" className="text-icoGray-400 dark:text-icoDarkMode-wolf" />
            <input
                data-testid="search-input"
                id={`search-box-input-${inputId}`}
                placeholder={placeholder}
                value={searchValue}
                className={clsx(
                    'bg-transparent placeholder-icoGray-400 dark:placeholder-icoDarkMode-wolf',
                    'w-full border-none px-0 py-4 text-xs',
                    'focus:outline-none focus:ring-0'
                )}
                onChange={(e) => setSearchValue(e.target.value)}
            />
        </div>
    );
}
